import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { services as reiServices } from "@/data/realEstateServices";
import SkylineOverlay from "@/components/brand/SkylineOverlay";

const Services = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.elevateglobalsolutions.net/services" },
    ],
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: reiServices.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.elevateglobalsolutions.net/services/${s.slug}`,
      name: s.title,
    })),
  };

  return (
    <>
      <Helmet>
        <title>Real Estate Acquisition Services | Elevate Global</title>
        <meta
          name="description"
          content="Cold calling, acquisition managers, appointment setters, VAs, CRM management, and full offshore acquisition teams for US real estate investors and wholesalers."
        />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/services" />
        <meta property="og:title" content="Real Estate Acquisition & Lead Gen Services for US Investors" />
        <meta property="og:description" content="Cold calling, acquisition managers, appointment setters, VAs, CRM management, and full offshore acquisition teams." />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/services" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="Real Estate Acquisition Services | Elevate Global Solutions" />
        <meta name="twitter:description" content="Every seat in your acquisition operation, built for US real estate." />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-secondary" />
        </div>
        <SkylineOverlay opacityClass="opacity-[0.06]" />
        <div className="container-narrow relative section-padding py-20 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground/90">
            Services for US Real Estate Investors
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-primary-foreground md:text-5xl">
            Every seat in your acquisition operation, built for US real estate
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/85">
            From cold calling to dispositions, we staff and run the offshore roles that fill your
            pipeline with off-market deals, at a fraction of US hiring cost.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book">
              <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
                Book a Free Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {reiServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group flex flex-col rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <div className="text-xs font-bold uppercase tracking-wider text-secondary">{s.shortLabel}</div>
                <h2 className="mt-2 text-xl font-semibold text-foreground group-hover:text-primary">
                  {s.title}
                </h2>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{s.heroSubhead}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.whatsIncluded.slice(0, 3).map((i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 text-sm font-medium text-primary">
                  Explore <ArrowRight className="ml-1 inline h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-accent/40">
        <div className="container-narrow text-center">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">Not sure where to start?</h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Book a 30-minute call. We'll show you which role unlocks the most pipeline for your
            current operation, and the order to add the rest.
          </p>
          <Link to="/book" className="mt-8 inline-block">
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              Book My Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Services;
